
/**
* @summary - Component that Provides License Type Code Listing Page
* @author Aro J
* @since 2024-06-10
*/

import { Box, Stack } from '@mui/material';
import axios from "axios";
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DefaultCard } from '../../assets/styled-components/card';
import { MasterGraph } from '../../utility/apiURLS';
import { getAuthToken, getcompanyId } from "../../utility/tokenUtility";
import CloseButton from '../commoncomponents/CloseButton';
import CreateNewButton from '../commoncomponents/CreateNewButton';
import CustomAgGrid from '../commoncomponents/CustomAgGrid';
import CustomDialog from '../commoncomponents/CustomDialog';
import PageTitle from '../commoncomponents/PageTitle';
import RefreshExportButtons from '../commoncomponents/RefreshExportButtons';
import SectionTitle from '../commoncomponents/SectionTitle';
import { useAccess } from "../../AccessContext";
import utilities from '../../utility/utilities';
import CustomSelectInput from '../commoncomponents/CustomSelectInput';

const columns = [
  // { field: 'service', headerName: 'Service', width: 150 },
  { field: 'licenseType', headerName: 'License Type', width: 150 },
  { field: 'licntypecode', headerName: 'License Type Code', width: 150 },
  { field: 'licntypevalue', headerName: 'License Type Description', width: 180 },
];

const Licensetypecodelist = () => {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [deletedRowDetails, setDeletedRowDetails] = useState({});
  const [successDialogOpen, setSuccessDialogOpen] = useState(false); // New state for success dialog
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const [gridApi, setGridApi] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [filterModel, setFilterModel] = useState([]);
  const [sortModel, setSortModel] = useState([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const [exportData, setExportData] = useState([]);
  const [exportCount, setExportCount] = useState(0)
  const [rowData, setRowData] = useState([]);
  const serviceOptions = [
    { label: "FEMS", value: "FEMS", id: 1 },
    { label: "BEMS", value: "BEMS", id: 2 },
  ];
  const [service, setService] = useState(serviceOptions[0]);


  const accessData = useAccess();
  const access = utilities.getScreenPermissions(accessData, "ENGM009");


  const token = getAuthToken("id_token");
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const companyId = getcompanyId();
  const fetchLicenseTypeCode = async () => {
    setRowData([]);
    setIsLoading(true);
    try {
      const response = await axios.get(`${MasterGraph}/api/graph/GetLincenseTypecodeDetailsbyCompanyId/${companyId}/${service.value}?page=${pageIndex}&size=${pageSize}&filterOptions=${JSON.stringify(
        filterModel
      )}&sortOptions=${JSON.stringify(sortModel)}`, config);
      setRowCount(response.data.totalCount);
      setExportCount(response.data.totalCount);
      setRowData(
        response.data.items.map((item) => {
          return {
            ...item,
          };
        })
      );

    } catch (error) {
      console.error("Error fetching asset categories:", error);
    }
    finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLicenseTypeCode();
  }, [filterModel, sortModel, pageIndex, pageSize]);

  const fetchExportdata = async () => {

    try {
      const token = getAuthToken("id_token");
      const companyId = getcompanyId("companyId");
      const config = {
        headers: {
          // Attach bearer token to request header
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.get(`${MasterGraph}/api/graph/GetExportLicense/${companyId}/${service.value}?filterOptions=${JSON.stringify(
        filterModel
      )}&sortOptions=${JSON.stringify(sortModel)}`, config);

      setExportData(
        response.data.map((item) => {
          return {
            ...item,

          };
        })
      );

    } catch (error) {
      console.error("Error fetching asset categories:", error);
    }
  };
  const handleRefresh = () => {
    setPageIndex(1);
    setPageSize(10);
    setFilterModel([])
    setSortModel([])
  };
  const handleEdit = (rowData) => {

    navigate("register", {
      state: {
        rowData: {
          ...rowData,
          service: { label: rowData.service, value: rowData.service, id: rowData.serviceId },
          licenseType: { label: rowData.licenseType, value: rowData.licenseType, id: rowData.maslicensetypeId }
        },
        isEditMode: true
      }
    });
  };
  const handleDelete = (row) => {
    setRowToDelete(row);
    setDeleteDialogOpen(true);
  };
  const confirmDelete = async () => {
    try {
      const token = getAuthToken("id_token");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      if (!rowToDelete || !rowToDelete.id) {
        console.error("Row to delete or its id is undefined");
        return;
      }

      await axios.delete(
        `${MasterGraph}/api/maslicensetypecodedetails/${rowToDelete.id}`,
        config
      );

      const updatedRows = rows.filter((row) => row.id !== rowToDelete.id);
      setRows(updatedRows);
      setDeletedRowDetails({
        code: rowToDelete.licntypecode,
        desc: rowToDelete.licntypevalue,
      });
      setSuccessDialogOpen(true); // Show success dialog
    } catch (error) {
      console.error("Error deleting License Type Code:", error);
      if (error.response && error.response.status === 400) {
        setErrorMessage("Record cannot be deleted as it is referred in another record.");
      } else {
        setErrorMessage("An error occurred while deleting the License Type Code. Please try again.");
      }
    } finally {
      setDeleteDialogOpen(false);
      setRowToDelete(null);
    }
    fetchLicenseTypeCode();
  };

  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false);
  };

  const handleCreateLicenseTypeCode = () => {
    navigate("register");
  };

  const handleServiceChange = (newValue) => {
    setService(newValue);
    setPageIndex(1);
    setPageSize(10);
    setFilterModel([])
    setSortModel([]);
  };

  return (
    <>
      <PageTitle title="License Type Code" />

      <DefaultCard>
        <Box
          mb={1}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
          }}
        >
          <SectionTitle title="License Type Code List" />
          <Stack direction={"row"} alignItems={"center"} gap={1}>
          <CustomSelectInput
                fullWidth={false}
                labelSize={"14px"}
                sx={{ width: 150 }}
                label={"Service"}

                options={serviceOptions}
                value={service}
                onChange={handleServiceChange}
                placeholder={"Select Service"}
                highlight={true}
              />
            {access?.canCreate && <CreateNewButton onClick={handleCreateLicenseTypeCode} />}
            <RefreshExportButtons
              export={filteredRows}
              exportFunction={fetchExportdata}
              exportData={exportData}
              refData={gridApi}
              handleRefresh={handleRefresh}
              title={"License Type Code"}
            />
          </Stack>
        </Box>
        <CustomAgGrid
           // rows={rows}
          rows={rowData}
          columns={columns}
          handleEdit={access?.canEdit && handleEdit}
          handleDelete={access?.canDelete && handleDelete}
          setGridApi={setGridApi}
          isLoading={isLoading}
            totalCount={rowCount}
            pageIndex={pageIndex}
            pagination={false}
            setFilterModel={setFilterModel}
            setSortModel={setSortModel}
            pageSize={pageSize}
            setPageIndex={setPageIndex}
            setPageSize={setPageSize}
           
        />
        <CustomDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={confirmDelete}
          content="Are you sure you want to delete?"
          cancelBtn='No'
          confirmBtn="Yes "
        />
        <CustomDialog
          open={successDialogOpen}
          onClose={handleSuccessDialogClose}
          content="Record deleted successfully."
          confirmBtn="Ok"
          onConfirm={handleSuccessDialogClose}
        />
        <CustomDialog
          open={!!errorMessage}
          onClose={() => setErrorMessage("")}
          onConfirm={() => setErrorMessage("")}
          content={errorMessage}
          cancelBtn="Ok"
        />
      </DefaultCard>
      <CloseButton />
    </>
  );
};

export default Licensetypecodelist;



